import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export const COOKIE_CONSENT_STORAGE_KEY = "pikra_cookie_consent";
export const COOKIE_CONSENT_VERSION = 1;

const DEFAULT_CONSENT = Object.freeze({
  necessary: true,
  analytics: false,
  marketing: false,
  decided: false,
  version: COOKIE_CONSENT_VERSION,
  timestamp: null,
});

const CookieConsentContext = createContext(null);

function safeReadStored() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.version !== COOKIE_CONSENT_VERSION) return null;
    return {
      ...DEFAULT_CONSENT,
      ...parsed,
      necessary: true,
      decided: Boolean(parsed.decided),
    };
  } catch {
    return null;
  }
}

function safeWriteStored(consent) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      COOKIE_CONSENT_STORAGE_KEY,
      JSON.stringify(consent),
    );
  } catch {
    // localStorage may be unavailable (private mode, quota). Best-effort only.
  }
}

function safeRemoveStored() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
  } catch {
    // Best-effort cleanup.
  }
}

export function CookieConsentProvider({ children }) {
  const [consent, setConsentState] = useState(
    () => safeReadStored() || { ...DEFAULT_CONSENT },
  );

  const persist = useCallback((next) => {
    const merged = {
      ...next,
      necessary: true,
      version: COOKIE_CONSENT_VERSION,
    };
    safeWriteStored(merged);
    setConsentState(merged);
  }, []);

  const acceptAll = useCallback(() => {
    persist({
      necessary: true,
      analytics: true,
      marketing: true,
      decided: true,
      timestamp: new Date().toISOString(),
    });
  }, [persist]);

  const rejectOptional = useCallback(() => {
    persist({
      necessary: true,
      analytics: false,
      marketing: false,
      decided: true,
      timestamp: new Date().toISOString(),
    });
  }, [persist]);

  const setConsent = useCallback(
    (partial) => {
      persist({
        necessary: true,
        analytics: Boolean(partial?.analytics),
        marketing: Boolean(partial?.marketing),
        decided: true,
        timestamp: new Date().toISOString(),
      });
    },
    [persist],
  );

  const revoke = useCallback(() => {
    safeRemoveStored();
    setConsentState({ ...DEFAULT_CONSENT });
  }, []);

  const value = useMemo(
    () => ({
      consent,
      hasConsented: consent.decided === true,
      acceptAll,
      rejectOptional,
      setConsent,
      revoke,
    }),
    [consent, acceptAll, rejectOptional, setConsent, revoke],
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error(
      "useCookieConsent must be used within a CookieConsentProvider",
    );
  }
  return ctx;
}
